import React, { createContext, useContext, useEffect, useMemo, useState, useCallback } from 'react';
import {
  collection,
  onSnapshot,
  addDoc,
  updateDoc,
  deleteDoc,
  doc,
  query,
  where
} from 'firebase/firestore';
import { db } from '../firebase/config';
import { useAuth } from './AuthContext';
import toast from 'react-hot-toast';

const PartnerContext = createContext();

export const usePartner = () => {
  const context = useContext(PartnerContext);
  if (!context) {
    throw new Error('usePartner must be used within a PartnerProvider');
  }
  return context;
};

const DEFAULT_PARTNERS = [
  {
    id: 'fallback-mscarte',
    name: 'MS Carte',
    url: 'https://mscarte.com/',
    domain: 'mscarte.com',
    logo: '/partners/mscarte.png',
    description: 'Plateforme Guinée Multiservices — paiements et services numériques.',
    isActive: true,
    isFallback: true
  },
  {
    id: 'fallback-tmrix',
    name: 'TMRIX',
    url: 'https://www.tmrix.com/fr',
    domain: 'tmrix.com',
    logo: '/partners/tmrix.png',
    description: 'Solutions digitales et accompagnement pour accélérer les projets.',
    isActive: true,
    isFallback: true
  }
];

const sortByCreatedAtDesc = (rows) =>
  [...rows].sort((a, b) => String(b.createdAt || '').localeCompare(String(a.createdAt || '')));

export const PartnerProvider = ({ children }) => {
  const { isAdmin } = useAuth();
  const admin = Boolean(isAdmin?.());
  const [partners, setPartners] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const q = admin
      ? query(collection(db, 'partners'))
      : query(collection(db, 'partners'), where('isActive', '==', true));

    const unsubscribe = onSnapshot(
      q,
      (snapshot) => {
        const rows = sortByCreatedAtDesc(
          snapshot.docs.map((item) => ({
            id: item.id,
            ...item.data()
          }))
        );
        setPartners(rows);
        setLoading(false);
      },
      (error) => {
        console.error('Erreur chargement partenaires:', error);
        setPartners([]);
        setLoading(false);
      }
    );
    return unsubscribe;
  }, [admin]);

  const createPartner = async ({ name, url, logo, description, domain }) => {
    if (!name?.trim()) {
      toast.error('Le nom du partenaire est requis');
      return null;
    }

    try {
      const cleanUrl = url?.trim() || '';
      let cleanDomain = (domain || '').trim();
      if (!cleanDomain && cleanUrl) {
        try {
          cleanDomain = new URL(cleanUrl).hostname.replace(/^www\./, '');
        } catch {
          cleanDomain = '';
        }
      }

      const payload = {
        name: name.trim(),
        url: cleanUrl,
        domain: cleanDomain,
        logo: logo || '',
        description: (description || '').trim(),
        isActive: true,
        createdAt: new Date().toISOString(),
        updatedAt: new Date().toISOString()
      };

      const refDoc = await addDoc(collection(db, 'partners'), payload);
      toast.success('Partenaire ajouté avec succès !');
      return { id: refDoc.id, ...payload };
    } catch (error) {
      console.error('Erreur création partenaire:', error);
      toast.error('Impossible d’ajouter le partenaire.');
      return null;
    }
  };

  const updatePartner = useCallback(async (partnerId, updateData) => {
    try {
      await updateDoc(doc(db, 'partners', partnerId), {
        ...updateData,
        updatedAt: new Date().toISOString()
      });
      toast.success('Partenaire mis à jour !');
      return true;
    } catch (error) {
      console.error('Erreur mise à jour partenaire:', error);
      toast.error('Impossible de mettre à jour le partenaire.');
      return false;
    }
  }, []);

  const deletePartner = useCallback(async (partnerId) => {
    try {
      await deleteDoc(doc(db, 'partners', partnerId));
      toast.success('Partenaire supprimé.');
      return true;
    } catch (error) {
      console.error('Erreur suppression partenaire:', error);
      toast.error('Impossible de supprimer le partenaire.');
      return false;
    }
  }, []);

  const togglePartnerActive = useCallback(async (partnerId) => {
    const current = partners.find((partner) => partner.id === partnerId);
    if (!current) {
      toast.error('Partenaire introuvable.');
      return false;
    }
    if (current.isFallback) {
      toast.error('Importez d’abord ce partenaire avant de le désactiver.');
      return false;
    }
    const nextActive = !(current.isActive !== false);
    return updatePartner(partnerId, { isActive: nextActive });
  }, [partners, updatePartner]);

  const getActivePartners = useCallback(() => {
    const active = partners.filter((partner) => partner.isActive !== false);
    return active.length > 0 ? active : DEFAULT_PARTNERS;
  }, [partners]);

  const value = useMemo(
    () => ({
      partners,
      loading,
      createPartner,
      updatePartner,
      deletePartner,
      togglePartnerActive,
      getActivePartners,
      defaultPartners: DEFAULT_PARTNERS
    }),
    [partners, loading, updatePartner, deletePartner, togglePartnerActive, getActivePartners]
  );

  return <PartnerContext.Provider value={value}>{children}</PartnerContext.Provider>;
};
